import { useContext, useEffect, useState } from "react";
import {
  getConfirmationMessages,
  createConfirmationMessages,
  updateConfirmationMessages,
  deleteConfirmationMessages,
  exportConfirmationMessages,
  importConfirmationMessages,
} from "../services/configurations/confirmation-messages";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "DOWNLOAD" | "IMPORT";

const handleConfirmationMessagesActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createConfirmationMessages,
        fnMsg: t("ConfirmationMessages.confirmationMessagesApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateConfirmationMessages,
        fnMsg: t("ConfirmationMessages.confirmationMessagesApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteConfirmationMessages,
        fnMsg: t("ConfirmationMessages.confirmationMessagesApiDeleteLabel"),
      };
    case "DOWNLOAD":
      return {
        actionFn: exportConfirmationMessages,
        fnMsg: t("ConfirmationMessages.confirmationMessagesApiDownloadLabel"),
      };
    case "IMPORT":
      return {
        actionFn: importConfirmationMessages,
        fnMsg: t("ConfirmationMessages.confirmationMessagesApiImportLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useConfirmationMessages = (type: UseType, messageId?: number) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const confirmationMessagesAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleConfirmationMessagesActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getConfirmationMessages(appid))
          .then((response) => {
            setData(response);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type, messageId]);

  return {
    data,
    confirmationMessagesAction,
    reloadFn,
  };
};
